import { Paths } from "@app/routes";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  Empty,
  ScrollArea,
} from "@atlas/core";
import { useSessionData, useTranslation } from "@atlas/sdk";
import type { IHubCatalog, IHubCatalogType } from "@models";
import { CatalogItemCard } from "@modules";
import { useHubPackageDeploymentSubscriptions } from "@query";
import { cva } from "class-variance-authority";
import { useCallback, useMemo } from "react";
import { createSearchParams, useNavigate } from "react-router";

const subscriptionsGridVariants = cva(
  "grid auto-rows-auto grid-cols-[repeat(auto-fill,345px)] gap-4 justify-start",
);

interface ISubscriptionsTabProps {
  searchQuery: string;
  catalogTypes: IHubCatalogType[];
}

export function SubscriptionsTab({
  searchQuery,
  catalogTypes,
}: ISubscriptionsTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useSessionData();
  const uid = (userId ?? "").trim();

  const {
    data: subscriptionsData,
    isLoading: deploymentsLoading,
    isError: deploymentsError,
  } = useHubPackageDeploymentSubscriptions(uid, { enabled: uid.length > 0 });

  const isLoading = deploymentsLoading;
  const isError = deploymentsError;

  const groupedCatalogs = useMemo(() => {
    let catalogs = subscriptionsData ?? [];
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.trim().toLowerCase();
      catalogs = catalogs.filter(
        (c: IHubCatalog) =>
          c.label?.toLowerCase().includes(lowerQuery) ||
          c.name?.toLowerCase().includes(lowerQuery),
      );
    }

    const grouped = new Map<number, IHubCatalog[]>();
    for (const catalog of catalogs) {
      const typeId = catalog.typeId;
      const typeCatalogs = grouped.get(typeId) ?? [];
      typeCatalogs.push(catalog);
      grouped.set(typeId, typeCatalogs);
    }
    return grouped;
  }, [subscriptionsData, searchQuery]);

  const handleMoreOptions = useCallback(
    (action: string, catalog?: IHubCatalog) => {
      if (action === "details" && catalog?.catalogId != null) {
        navigate({
          pathname: `/${Paths.CatalogManager}/${Paths.CatalogDetails}`,
          search: createSearchParams({
            tab: "subscriptions",
            catalogId: String(catalog.catalogId),
            version: String(catalog.version),
          }).toString(),
        });
      }
    },
    [navigate],
  );

  const emptyMessage = useMemo(() => {
    if (uid.length === 0) {
      return t("hub.catalog_manager.subscriptions_empty");
    }
    if (isError) {
      return t("hub.catalog_manager.reviews_catalog_error", {
        defaultValue: "Could not load catalog details.",
      });
    }
    if (searchQuery.trim()) {
      return t("hub.catalog_manager.no_results", {
        defaultValue: "No catalogs match your search.",
      });
    }
    return t("hub.catalog_manager.subscriptions_empty");
  }, [uid.length, isError, searchQuery, t]);

  const hasAnyCatalogs = Array.from(groupedCatalogs.values()).some(
    (catalogs) => catalogs.length > 0,
  );

  return (
    <ScrollArea
      className="min-h-0 min-w-0 flex-1"
      viewportClassName="flex flex-col gap-8 overscroll-y-contain pb-2 [-webkit-overflow-scrolling:touch]">
      {!hasAnyCatalogs ? (
        <Card
          variant="content"
          className="border-0 shadow-none"
          isLoading={isLoading}>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Empty className="text-muted-foreground">{emptyMessage}</Empty>
            </div>
          </CardContent>
        </Card>
      ) : (
        catalogTypes.map((type) => {
          const typeCatalogs = groupedCatalogs.get(type.typeId ?? -1) ?? [];
          if (typeCatalogs.length === 0) return null;

          return (
            <section
              key={type.typeId ?? type.name}
              className="flex flex-col gap-4">
              <Card
                variant="content"
                className="border-0 shadow-none"
                isLoading={isLoading}>
                <CardHeader className="flex flex-row items-start justify-between gap-2 sticky top-0 z-1 bg-background py-2 mr-5 text-lg font-bold">
                  <div className="flex flex-col text-left gap-2">
                    <CardTitle>{type.label}</CardTitle>
                    {type.description && (
                      <CardDescription>{type.description}</CardDescription>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea
                    className="w-full min-w-0 pb-1"
                    viewportClassName="overscroll-x-contain [-webkit-overflow-scrolling:touch]">
                    <div className={cn(subscriptionsGridVariants())}>
                      {typeCatalogs.map((catalog) => (
                        <CatalogItemCard
                          key={`${catalog.catalogId}-${catalog.version}`}
                          catalog={catalog}
                          onMoreOptions={handleMoreOptions}
                          ownerActionsMenuItems={[]}
                          showLifecycleBadge
                          showOwnerActionsMenu={false}
                          showSubscribeAction={false}
                        />
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </section>
          );
        })
      )}
    </ScrollArea>
  );
}
